import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { T as Txt } from "@/context/TextEditContext";

const inputCls =
  "w-full px-3 py-2.5 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-sm";

// Field components live at module level (NOT inside the page component) so
// their identity is stable across re-renders — otherwise React remounts the
// input on every keystroke and the mobile keyboard closes.
const Field = ({ label, optional, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
      {label}{" "}
      {optional ? (
        <span className="text-stone-400 normal-case font-medium">(optional)</span>
      ) : (
        <span className="text-red-600">*</span>
      )}
    </span>
    {children}
  </label>
);

// Inputs are UNCONTROLLED (defaultValue + ref store): typing triggers zero
// React re-renders, so nothing can steal focus / close the mobile keyboard.
const Text = ({ k, label, area, type = "text", optional, defaultValue, onChange }) => (
  <Field label={label} optional={optional}>
    {area ? (
      <textarea rows={3} defaultValue={defaultValue} onChange={onChange} data-testid={`job-${k}`} className={inputCls} />
    ) : (
      <input type={type} defaultValue={defaultValue} onChange={onChange} data-testid={`job-${k}`} className={inputCls} />
    )}
  </Field>
);

const YesNo = ({ k, label, optional, value, onPick }) => (
  <Field label={label} optional={optional}>
    <div className="flex gap-2">
      {["yes", "no"].map((v) => (
        <button
          key={v}
          type="button"
          data-testid={`job-${k}-${v}`}
          onClick={() => onPick(v)}
          className={`px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest border-2 transition-colors ${
            value === v
              ? "bg-brand-red text-white border-brand-red"
              : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
          }`}
        >
          {v === "yes" ? "Yes" : "No"}
        </button>
      ))}
    </div>
  </Field>
);

const Section = ({ title }) => (
  <div className="font-display text-brand-red text-xl pt-4 border-t border-stone-200">{title}</div>
);

export default function JobsPage() {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const vals = useRef({});
  const [f, setF] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    vals.current[k] = e.target.value;
  };
  const pick = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const T = (k, label, extra = {}) => (
    <Text k={k} label={label} defaultValue={vals.current[k] || ""} onChange={set(k)} {...extra} />
  );
  const YN = (k, label, extra = {}) => (
    <YesNo k={k} label={label} value={f[k]} onPick={pick(k)} {...extra} />
  );

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/jobs/apply", { ...vals.current, ...f });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please fill in every field before submitting");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex justify-center">
      <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper jobs-solid">
        <div className="bg-brand-red px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
          <button onClick={() => navigate("/")} data-testid="jobs-back-btn" className="text-white/90 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="font-display text-brand-yellow text-xl flex items-center gap-2">
            <Briefcase className="w-5 h-5" /> Join Our Team
          </div>
        </div>

        {done ? (
          <div className="px-6 py-16 text-center space-y-4" data-testid="job-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-600 flex items-center justify-center">
              <BadgeCheck className="w-8 h-8 text-white" />
            </div>
            <div className="font-hand text-4xl text-brand-red">Application sent!</div>
            <p className="text-sm text-stone-600 leading-relaxed">
              Thanks for applying to Blossom's Kitchen — we've received your application
              and will reach out soon.
            </p>
            <Link to="/" className="inline-block bg-brand-red text-white px-6 py-3 rounded-full text-xs font-black uppercase tracking-widest">
              Back to Menu
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="px-4 py-5 space-y-4 pb-16" noValidate>
            {settings?.hiring_open === false && (
              <div
                className="rounded-2xl border-2 border-brand-yellow bg-brand-yellow/15 p-4 text-sm text-stone-800 leading-relaxed"
                data-testid="jobs-closed-notice"
              >
                <div className="font-display text-brand-red text-xl leading-tight mb-1">
                  <Txt k="jobs.closed_title" d="We're not hiring right now" />
                </div>
                <Txt
                  k="jobs.closed_body"
                  d="Our team is full at the moment, but positions open up often. You're welcome to send your application anyway and we'll keep it on file for the next opening."
                />
              </div>
            )}
            <p className="text-xs text-stone-500 leading-relaxed">
              Employment application — all fields are required except the Previous
              Employment section.
            </p>
            {T("name", "Full Name")}
            {YN("is_adult", "Are you 18 years or older?")}
            {T("address", "Street Address")}
            <div className="grid grid-cols-2 gap-3">
              {T("city_state", "City / State")}
              {T("zip_code", "Zip / Postal Code")}
            </div>
            {T("phone", "Phone", { type: "tel" })}
            {T("email", "Email Address", { type: "email" })}
            {T("position", "Position Applied For")}
            <div className="grid grid-cols-2 gap-3">
              {T("salary_desired", "Salary Desired")}
              {T("start_date", "Available to Begin")}
            </div>

            <Section title="Education" />
            {T("education_school", "School Name & Address")}
            <div className="grid grid-cols-2 gap-3">
              {T("education_years", "Years Completed")}
              {T("education_degree", "Major or Degree")}
            </div>

            <Section title="Background" />
            {YN("convicted", "Ever convicted of a crime/felony?")}
            {f.convicted === "yes" &&
              T("convicted_explain", "Please explain (won't necessarily exclude you)", { area: true })}
            {YN("drivers_license", "Valid driver's license?")}
            {f.drivers_license === "yes" && T("license_state", "State of Issue")}

            <Section title="Most Recent Employment (optional)" />
            {T("employer_name", "Name of Employer", { optional: true })}
            {T("supervisor", "Name of Last Supervisor", { optional: true })}
            <div className="grid grid-cols-2 gap-3">
              {T("employed_from", "Employed From", { optional: true })}
              {T("employed_to", "Employed To", { optional: true })}
            </div>
            {T("employer_phone", "Employer Phone", { type: "tel", optional: true })}
            {T("job_title", "Last Job Title", { optional: true })}
            {T("reason_leaving", "Reason for Leaving (be specific)", { area: true, optional: true })}
            {T("duties", "Jobs held, duties performed, skills used or learned", { area: true, optional: true })}
            {YN("may_contact", "May we contact this employer?", { optional: true })}

            <Section title="References (not related to you, known 1+ year)" />
            {T("ref1_name", "Reference 1 — Name")}
            <div className="grid grid-cols-3 gap-2">
              {T("ref1_phone", "Phone", { type: "tel" })}
              {T("ref1_relationship", "Relationship")}
              {T("ref1_years", "Years Known")}
            </div>
            {T("ref2_name", "Reference 2 — Name")}
            <div className="grid grid-cols-3 gap-2">
              {T("ref2_phone", "Phone", { type: "tel" })}
              {T("ref2_relationship", "Relationship")}
              {T("ref2_years", "Years Known")}
            </div>

            <Section title="Final Step" />
            {T("additional_info", "Anything else describing your qualifications", { area: true })}
            <div className="text-[11px] text-stone-500 leading-relaxed bg-stone-50 border border-stone-200 rounded-lg p-3">
              I certify that the facts contained in this application are true and complete
              to the best of my knowledge and understand that, if employed, falsified
              statements shall be grounds for dismissal. I authorize investigation of all
              statements contained herein.
            </div>
            {T("signature", "Signature (type your full name)")}
            <button
              type="submit"
              disabled={sending}
              data-testid="job-submit-btn"
              className="w-full bg-brand-red text-white py-3.5 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-50"
            >
              {sending ? "Sending..." : "Submit Application"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
